// Chapman University: public college page (lane 2, #2745), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Chapman's Common Data Set 2025-2026 (Institutional Research): C1
//    applicants, admits and enrollees for Fall 2025, C21 early decision
//    counts, G1/G5 2026-2027 costs. Quirks: the PDF's text layer garbles some
//    fonts and check boxes. C1 gives no total line, so the totals are the
//    sums of its rows (5,212 + 9,548 + 9 applied; 3,649 + 6,988 + 5
//    admitted; 622 + 3 + 1,099 + 2 + 1 enrolled). The C1 residency table is
//    blank. C21's "For the Fall 202_ entering class" lost its last digit in
//    the text layer; in the 2025-2026 edition that is the Fall 2025 class.
//    The test policy is read from Chapman's admissions pages.
//  - Chapman Admission: First-Year Students (Fall 2027 deadlines and
//    decision timing), Early Application Options, Test Optional, Creative
//    Supplement. The First-Year page says Early Decision II decisions go out
//    in late January; the Early Options page says early to mid-February.
//  - College Scorecard API, school id 111948 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Chapman Common Data Set 2025-2026',
        url: 'https://www.chapman.edu/campus-services/institutional-research/_files/cds/cds_2025-2026.pdf',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Chapman Admission, First-Year Students',
        url: 'https://www.chapman.edu/admission/undergraduate/how-to-apply/first-year/index.aspx',
        read: '2026-10-01',
    },
    early: {
        label: 'Chapman Admission, Early Application Options',
        url: 'https://www.chapman.edu/admission/undergraduate/how-to-apply/first-year/early-options.aspx',
        read: '2026-10-01',
    },
    testing: {
        label: 'Chapman Admission, Test Optional',
        url: 'https://www.chapman.edu/admission/undergraduate/how-to-apply/test-optional.aspx',
        read: '2026-10-01',
    },
    creative: {
        label: 'Chapman Admission, Creative Supplement',
        url: 'https://www.chapman.edu/admission/undergraduate/how-to-apply/creative-supplement.aspx',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?111948-Chapman-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'chapman-university',
    kbId: 'chapman_university',
    name: 'Chapman University',
    shortName: 'Chapman',
    location: 'Orange, CA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Chapman brand colors); decoration only.
    branding: {
        primary: '#A50034',
        secondary: '#231F20',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/chapman_university_logo.png',
    },

    // The direct answer and short FAQ (#4229, founder 2026-10-14: content
    // only, no title change). Every figure restates one the page already
    // shows from the source named beside it; CollegePages.test.jsx fails a
    // number the rest of the page does not hold.
    // The search it answers: chapman university acceptance rate.
    faq: {
        answer: { text: 'Chapman University admitted 72.1% of first-year applicants for fall 2025, 10,642 of the 14,769 who applied, according to its Common Data Set 2025-2026.', source: 'cds', where: 'section C1 (sum of its rows; it gives no total line)' },
        questions: [
            { q: 'What is the Chapman acceptance rate?', a: 'For fall 2025 entry, Chapman admitted 10,642 of 14,769 first-year applicants, an acceptance rate of 72.1%, and 1,727 enrolled.', source: 'cds', where: 'section C1' },
            { q: 'What is the Chapman early decision acceptance rate?', a: 'For fall 2025, Chapman admitted 145 of 282 Early Decision applicants, 51.4%. The Common Data Set gives no early action counts, so Chapman publishes no early action rate.', source: 'cds', where: 'section C21' },
            { q: 'What are Chapman\'s application deadlines for fall 2027?', a: 'Early Action and Early Decision I are due November 1. Early Decision II and Regular Decision are due January 15. Early Decision I decisions come in mid-December, Early Action decisions start in late December, and Regular Decision decisions come in mid-March.', source: 'firstYear' },
            { q: 'Does Chapman require the SAT or ACT?', a: 'No. Chapman\'s undergraduate programs are test-optional: you choose whether your SAT or ACT scores are reviewed. If you send them, Chapman superscores.', source: 'testing' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 14769,
        admitted: 10642,
        enrolled: 1727,
        source: 'cds',
        where: 'section C1 (sum of its rows; it gives no total line)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Chapman\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        note: 'The Common Data Set gives early decision counts but no early action counts, so there is no early action rate.',
        rows: [
            { plan: 'Early Decision', applied: 282, admitted: 145 },
        ],
        source: 'cds',
        where: 'section C21',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to a first-choice major. These programs also need a Creative Supplement, due five days after your application:',
        rows: [
            { school: 'Animation and Visual Effects, Art, Creative Producing, Design, Documentary, Sports Media and Journalism, Film and Television Production, Screen Acting, Theatre Performance, Writing for Film and Television (BFA)', requirement: 'A Creative Supplement reviewed by the department and faculty.' },
            { school: 'Dance, Film and Media Studies, Music, Public Relations, Advertising and Entertainment Marketing, Theatre (BA); Composition, Performance, Pre-Music Education Certification (BM)', requirement: 'A Creative Supplement reviewed by the department and faculty.' },
        ],
        note: 'A Creative Supplement is needed only for your first-choice major. Chapman recommends applying by November 1 if you apply to one of these programs.',
        source: 'creative',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'Late December, then rolling into early February', binding: false },
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Mid-December', binding: true },
            { plan: 'Early Decision II', deadline: 'January 15', decision: 'Late January to mid-February', binding: true },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'Mid-March', binding: false },
        ],
        note: 'Chapman\'s First-Year page says Early Decision II decisions go out in late January, and its Early Options page says early to mid-February.',
        source: 'firstYear',
        binding: 'Early Decision I and II are binding: you must enroll if admitted to your first-choice major (not if admitted to your second choice), and you may not apply early decision elsewhere. If you are admitted but cannot afford to attend, you can be released from the agreement.',
        bindingSource: 'early',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Chapman\'s undergraduate programs are test-optional: you choose on your application whether your SAT or ACT scores are reviewed. If you send them, Chapman superscores, and scores you enter in the Common App are accepted.',
        source: 'testing',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'testing',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 69990 },
            { item: 'Required fees', amount: 404 },
            { item: 'Food and housing (on campus, first year)', amount: 18900 },
            { item: 'Books and supplies', amount: 1600 },
            { item: 'Transportation', amount: 1300 },
            { item: 'Other expenses', amount: 2182 },
        ],
        total: 94376,
        note: 'For a first-year student living on campus. The Common Data Set lists food and housing for other undergraduates at $21,700.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 46708,
            byIncome: [
                { income: '$0 to $30,000', amount: 34494 },
                { income: '$30,001 to $48,000', amount: 30442 },
                { income: '$48,001 to $75,000', amount: 31307 },
                { income: '$75,001 to $110,000', amount: 41191 },
                { income: 'Over $110,000', amount: 55261 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Chapman\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
